import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

// Une barre d'onglets qui ne cache jamais un onglet.
//
// Vécu 29.09, fiche d'un besoin : « Documents » coupé au bord de l'écran. La
// barre défilait, mais sa barre de défilement était masquée : rien ne disait
// qu'il restait un onglet. Ce qui ne tient pas sur la ligne passe dans
// « Plus », et l'onglet ouvert reste TOUJOURS visible — choisi dans « Plus »,
// il prend la place du dernier onglet qui tient.
//
// Les onglets écartés restent rendus, invisibles et hors du flux : c'est leur
// largeur réelle (compte compris) qui décide de ce qui tient, pas une estimation.
//
// onglets : [{ key, icon, label, count? }]
export default function BarreOnglets({ onglets, actif, onChoisir, className = '' }) {
  const barre = useRef(null);
  const plus = useRef(null);
  const mesures = useRef({});
  const menu = useRef(null);
  const [caches, setCaches] = useState([]);
  const [ouvert, setOuvert] = useState(false);

  const signature = onglets.map(o => `${o.key}:${o.label}:${o.count ?? ''}`).join('|');

  const calculer = useCallback(() => {
    const el = barre.current;
    if (!el) return;
    const style = getComputedStyle(el);
    const dispo = el.clientWidth - parseFloat(style.paddingLeft || 0) - parseFloat(style.paddingRight || 0);
    const ecart = parseFloat(style.columnGap) || 0;
    const largeur = key => mesures.current[key]?.offsetWidth || 0;
    const total = onglets.reduce((s, o, i) => s + largeur(o.key) + (i ? ecart : 0), 0);
    let nouveaux = [];
    if (total > dispo + 0.5) {
      let reste = dispo - (plus.current?.offsetWidth || 0);
      const ouvertIci = onglets.some(o => o.key === actif);
      if (ouvertIci) reste -= largeur(actif) + ecart;
      const tiennent = new Set(ouvertIci ? [actif] : []);
      for (const o of onglets) {
        if (o.key === actif) continue;
        const w = largeur(o.key) + ecart;
        if (w > reste) break;
        tiennent.add(o.key);
        reste -= w;
      }
      nouveaux = onglets.filter(o => !tiennent.has(o.key)).map(o => o.key);
    }
    setCaches(prec => (prec.join('|') === nouveaux.join('|') ? prec : nouveaux));
    // `signature` suit les libellés et les comptes, qui changent les largeurs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, actif]);

  useLayoutEffect(() => { calculer(); }, [calculer]);

  useEffect(() => {
    const el = barre.current;
    if (!el) return undefined;
    let obs = null;
    if (typeof ResizeObserver !== 'undefined') {
      obs = new ResizeObserver(() => calculer());
      obs.observe(el);
    } else {
      window.addEventListener('resize', calculer);
    }
    // Les polices arrivent après le premier rendu : les largeurs changent.
    if (document.fonts?.ready) document.fonts.ready.then(calculer).catch(() => {});
    return () => { if (obs) obs.disconnect(); else window.removeEventListener('resize', calculer); };
  }, [calculer]);

  useEffect(() => {
    if (!ouvert) return undefined;
    const dehors = e => {
      if (!menu.current?.contains(e.target) && !plus.current?.contains(e.target)) setOuvert(false);
    };
    const echap = e => {
      if (e.key === 'Escape') { setOuvert(false); plus.current?.querySelector('button')?.focus(); }
    };
    document.addEventListener('mousedown', dehors);
    document.addEventListener('keydown', echap);
    menu.current?.querySelector('[role="menuitem"]')?.focus();
    return () => { document.removeEventListener('mousedown', dehors); document.removeEventListener('keydown', echap); };
  }, [ouvert]);

  useEffect(() => { if (caches.length === 0) setOuvert(false); }, [caches.length]);

  const choisir = key => { setOuvert(false); onChoisir(key); };

  const flecheMenu = e => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = [...(menu.current?.querySelectorAll('[role="menuitem"]') || [])];
    const i = items.indexOf(document.activeElement);
    const suivant = e.key === 'ArrowDown' ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
    items[suivant]?.focus();
  };

  const contenu = o => (
    <>
      <i className={`bi bi-${o.icon}`} aria-hidden="true" /> {o.label}
      {o.count != null && <span className="bd-tab-count">{o.count}</span>}
    </>
  );

  const horsLigne = onglets.filter(o => caches.includes(o.key));

  return (
    <div className={`bd-tabs bd-tabs--plus ${className}`} ref={barre}>
      {onglets.map(o => {
        const cache = caches.includes(o.key);
        return (
          <button key={o.key} type="button"
            ref={el => { mesures.current[o.key] = el; }}
            className={`bd-tab ${actif === o.key ? 'active' : ''} ${cache ? 'bd-tab--mesure' : ''}`}
            aria-hidden={cache || undefined} tabIndex={cache ? -1 : undefined}
            onClick={() => onChoisir(o.key)}>
            {contenu(o)}
          </button>
        );
      })}
      <div ref={plus} className={`bd-tab-plus ${caches.length ? '' : 'bd-tab--mesure'}`} aria-hidden={caches.length ? undefined : true}>
        <button type="button" className="bd-tab" tabIndex={caches.length ? undefined : -1}
          aria-haspopup="menu" aria-expanded={ouvert} onClick={() => setOuvert(v => !v)}>
          Plus <span className="bd-tab-count">{caches.length}</span>
          <i className={`bi bi-chevron-down bd-tab-plus__chevron ${ouvert ? 'is-open' : ''}`} aria-hidden="true" />
        </button>
        {ouvert && horsLigne.length > 0 && (
          <div ref={menu} className="erp-select__menu bd-tab-plus__menu" role="menu" onKeyDown={flecheMenu}>
            {horsLigne.map(o => (
              <button key={o.key} type="button" role="menuitem" className="erp-select__option" onClick={() => choisir(o.key)}>
                <span className="bd-tab-plus__libelle"><i className={`bi bi-${o.icon}`} aria-hidden="true" /> {o.label}</span>
                {o.count != null && <span className="bd-tab-count">{o.count}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
